/**
 * AI Service for Dashboard Voice Search
 * Lightweight AI assistant that searches for teams/players and navigates to their pages.
 */

import { $apiV1, $apiV2 } from '../../../config/api';
import { consumeRetryQueue, trackSearchedPlayer } from '../../events-explorer/services/openaiVoiceService';

// Phase 10 (2026-08-14): default switched to Sonnet 5 (see aiService.js for
// rationale). Dashboard AI does simple search+navigate — low effort keeps it snappy.
const ANTHROPIC_MODEL = process.env.REACT_APP_ANTHROPIC_MODEL || 'claude-sonnet-5';

const SECTION_ROUTES = {
  'dashboard': '/dashboard',
  'home': '/dashboard',
  'favorites': '/favorites',
  'my favorites': '/favorites',
  'playlists': '/my-playlists',
  'my playlists': '/my-playlists',
  'smrt explorer': '/smrt-explorer',
  'events explorer': '/smrt-explorer',
  'smrt search': '/smrt-search',
  'search': '/smrt-search',
  'compare': '/compare',
  'comparison': '/compare',
  'competitions': '/tournaments',
  'tournaments': '/tournaments',
  'leagues': '/tournaments',
  'platform setup': '/platform-setup',
  'setup': '/platform-setup',
  'my account': '/my-account',
  'account': '/my-account',
  'contact': '/contact-us',
  'contact us': '/contact-us',
};

const SYSTEM_PROMPT_BASE = `You are a football assistant on the SmrtStats dashboard. You help users find teams, players, and competitions, and navigate anywhere in the app.

CAPABILITIES:
1. **Search & navigate to teams/players** — use search_teams / search_players, then navigate_to_page
2. **Search & navigate to competitions/leagues** — use search_competitions to find a league, then navigate_to_competition
3. **Navigate to any app section** — use navigate_to_section for menu items like Favorites, My Playlists, Smrt Explorer, Compare, Competitions, etc.

RULES:
- When the user asks about a team or player, search first. If a single clear match is found, navigate immediately. If multiple matches, present the top options.
- When the user asks about a competition/league (e.g. "Premier League", "La Liga"), use search_competitions then navigate_to_competition with the preferred season.
- When the user says "take me to favorites", "open smrt explorer", "go to my playlists", etc., use navigate_to_section directly.
- If the user specifies a particular tab (e.g. "show me their events", "shots"), include the tab parameter.

Available sections for navigate_to_section: dashboard, favorites, playlists, smrt explorer, smrt search, compare, competitions, platform setup, my account, contact us

Available tabs for team pages: overview, players, matches, events, shots, crosses
Available tabs for player pages: overview, matches, statistics, events, shots, crosses

CRITICAL: When you navigate somewhere, use PRESENT tense — the navigation happens instantly before you respond. Say "Here's the page" or "Opening Red Star Belgrade's statistics" — NEVER "I'll take you" or "Let me navigate you" (future tense). The user is already on the page by the time they read/hear your response.
If you can't find what the user is looking for, say so clearly.
Keep responses short and helpful.`;

const VOICE_PROMPT_SUFFIX = `\n\nVOICE MODE IS ACTIVE. You MUST include a [VOICE_RESPONSE]...[/VOICE_RESPONSE] section at the END of EVERY response. This section contains a conversational spoken version — as if talking to a colleague on a Zoom call. Keep it brief (1-2 sentences), natural, use contractions. The main text outside the tags stays detailed for screen reading.`;

const TOOLS = [
  {
    name: "search_teams",
    description: "Search for football teams by name. Returns a list of matching teams with their IDs, names, countries, and logos.",
    input_schema: {
      type: "object",
      properties: {
        query: {
          type: "string",
          description: "Team name or part of name to search for"
        }
      },
      required: ["query"]
    }
  },
  {
    name: "search_players",
    description: "Search for football players by name. Returns a list of matching players with their IDs, names, teams, and positions.",
    input_schema: {
      type: "object",
      properties: {
        query: {
          type: "string",
          description: "Player name or part of name to search for"
        }
      },
      required: ["query"]
    }
  },
  {
    name: "navigate_to_page",
    description: "Navigate the user to a team or player detail page, optionally opening a specific tab.",
    input_schema: {
      type: "object",
      properties: {
        page_type: {
          type: "string",
          enum: ["team", "player"],
          description: "Type of page to navigate to"
        },
        id: {
          type: "integer",
          description: "Team or player ID from search results"
        },
        name: {
          type: "string",
          description: "Team or player name (for display in response)"
        },
        tab: {
          type: "string",
          description: "Tab to open. Team: overview|players|matches|events|shots|crosses. Player: overview|matches|statistics|events|shots|crosses. Omit for default (overview)."
        }
      },
      required: ["page_type", "id", "name"]
    }
  },
  {
    name: "navigate_to_section",
    description: "Navigate to an app section/menu item. Use this when the user wants to go to Favorites, My Playlists, Smrt Explorer, Smrt Search, Compare, Competitions, Platform Setup, My Account, or Contact Us.",
    input_schema: {
      type: "object",
      properties: {
        section: {
          type: "string",
          description: "Section name: dashboard, favorites, playlists, smrt explorer, smrt search, compare, competitions, platform setup, my account, contact us"
        }
      },
      required: ["section"]
    }
  },
  {
    name: "search_competitions",
    description: "Search for football competitions/leagues by name (e.g. 'Premier League', 'La Liga', 'Champions League'). Returns matching competitions with their seasons.",
    input_schema: {
      type: "object",
      properties: {
        query: {
          type: "string",
          description: "Competition or league name to search for"
        }
      },
      required: ["query"]
    }
  },
  {
    name: "navigate_to_competition",
    description: "Navigate to a competition/league page for a specific season.",
    input_schema: {
      type: "object",
      properties: {
        season_id: {
          type: "integer",
          description: "Season ID from the search_competitions results"
        },
        name: {
          type: "string",
          description: "Competition name (for display in response)"
        }
      },
      required: ["season_id", "name"]
    }
  }
];

/**
 * Execute a tool call
 */
export const executeTool = async (toolName, toolInput, navigate) => {
  switch (toolName) {
    case 'search_teams': {
      const response = await $apiV2.get('/platform/team', {
        params: { search: toolInput.query }
      });
      const results = response?.data?.results || [];
      return results.slice(0, 8).map(t => ({
        id: t.id,
        name: t.name,
        country: t.country?.name || '',
        logo: t.logo || ''
      }));
    }
    case 'search_players': {
      const searchPlayer = async (query) => {
        const response = await $apiV2.get('/platform/player', {
          params: { search: query }
        });
        return response?.data?.results || [];
      };
      const formatPlayer = (p) => ({
        id: p.id,
        name: p.name,
        team: p.team?.name || '',
        position: p.position || ''
      });

      let results = await searchPlayer(toolInput.query);
      if (results.length === 0) {
        // Fallback: retry with up to 5 variations from the voice correction pipeline
        const retries = consumeRetryQueue();
        for (const variation of retries) {
          results = await searchPlayer(variation);
          if (results.length > 0) break;
        }
      }
      // Track successfully found player names for search history biasing
      if (results.length > 0) {
        results.slice(0, 3).forEach(p => { if (p.name) trackSearchedPlayer(p.name); });
      }
      return results.slice(0, 8).map(formatPlayer);
    }
    case 'navigate_to_page': {
      const { page_type, id, tab } = toolInput;
      if (!page_type || id == null) {
        return { error: 'Missing required page_type or id parameter' };
      }
      const validTypes = ['team', 'player'];
      if (!validTypes.includes(page_type)) {
        return { error: `Invalid page_type "${page_type}". Must be one of: ${validTypes.join(', ')}` };
      }
      const path = `/tournaments/${page_type}/${id}${tab ? `?tab=${tab}` : ''}`;
      if (navigate) {
        navigate(path);
      }
      return { success: true, navigated_to: path };
    }
    case 'navigate_to_section': {
      const sectionKey = (toolInput.section || '').toLowerCase().trim();
      const path = SECTION_ROUTES[sectionKey];
      if (!path) {
        return { error: `Unknown section "${toolInput.section}". Available: ${Object.keys(SECTION_ROUTES).join(', ')}` };
      }
      if (navigate) {
        navigate(path);
      }
      return { success: true, navigated_to: path, section: toolInput.section };
    }
    case 'search_competitions': {
      const response = await $apiV2.get('/platform/competition', {
        params: { search: toolInput.query }
      });
      const results = response?.data?.results || [];
      return results.slice(0, 6).map(c => {
        const seasons = c.seasons || [];
        // Pick preferred season: current/active first, then most recent
        const preferred = seasons.find(s => s.is_current || s.current || s.is_active || s.active)
          || seasons[0];
        return {
          id: c.id,
          name: c.name,
          country: c.country?.name || '',
          logo: c.logo || '',
          preferred_season: preferred ? { id: preferred.id, year: preferred.year_season } : null,
          seasons: seasons.slice(0, 5).map(s => ({ id: s.id, year: s.year_season }))
        };
      });
    }
    case 'navigate_to_competition': {
      const { season_id } = toolInput;
      if (!season_id) {
        return { error: 'Missing required season_id parameter' };
      }
      const path = `/tournaments/competition/${season_id}`;
      if (navigate) {
        navigate(path);
      }
      return { success: true, navigated_to: path };
    }
    default:
      return { error: `Unknown tool: ${toolName}` };
  }
};

const apiPostWithRetry = async (url, data, { maxRetries = 2, onStatusUpdate } = {}) => {
  let lastError;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const response = await $apiV1.post(url, data, { timeout: 60000 });
      return response.data;
    } catch (error) {
      const status = error.response?.status;
      if (status === 429 && attempt < maxRetries) {
        const retryAfter = error.response?.headers?.['retry-after'];
        const waitMs = retryAfter ? parseInt(retryAfter, 10) * 1000 : Math.pow(2, attempt) * 1000;
        if (onStatusUpdate) {
          onStatusUpdate(`Rate limited, retrying in ${Math.round(waitMs / 1000)}s...`);
        }
        await new Promise(resolve => setTimeout(resolve, waitMs));
        continue;
      }
      lastError = error;
      if (attempt === maxRetries) break;
    }
  }
  const message = lastError?.response?.data?.error?.message || lastError?.message || 'Request failed after retries';
  throw new Error(message);
};

const callAnthropic = async (messages, onStatusUpdate, voiceMode = false) => {
  const systemPrompt = voiceMode ? SYSTEM_PROMPT_BASE + VOICE_PROMPT_SUFFIX : SYSTEM_PROMPT_BASE;
  return apiPostWithRetry('/ai/messages', {
    model: ANTHROPIC_MODEL,
    max_tokens: 4096, // Sonnet 5 thinks by default; the cap covers thinking + text
    output_config: { effort: 'low' },
    system: [{ type: "text", text: systemPrompt, cache_control: { type: "ephemeral" } }],
    tools: TOOLS,
    messages
  }, { onStatusUpdate });
};

const MAX_TOOL_ROUNDS = 5;

const processAIResponse = async (response, messages, navigate, onStatusUpdate, voiceMode = false, depth = 0) => {
  const content = response.content || [];
  const toolResults = [];
  let textResponse = '';
  const actions = [];

  for (const block of content) {
    if (block.type === 'text') {
      textResponse += block.text;
    } else if (block.type === 'tool_use') {
      if (onStatusUpdate) {
        const labels = {
          search_teams: `Searching teams: "${block.input.query}"`,
          search_players: `Searching players: "${block.input.query}"`,
          search_competitions: `Searching competitions: "${block.input.query}"`,
          navigate_to_page: `Navigating to ${block.input.name}...`,
          navigate_to_section: `Opening ${block.input.section}...`,
          navigate_to_competition: `Opening ${block.input.name}...`
        };
        onStatusUpdate(labels[block.name] || `Running ${block.name}...`);
      }
      let result;
      try {
        result = await executeTool(block.name, block.input, navigate);
      } catch (toolError) {
        console.error(`[Dashboard AI Tool] ${block.name} ERROR:`, toolError);
        result = { error: toolError.message || 'Tool execution failed' };
      }
      toolResults.push({
        type: 'tool_result',
        tool_use_id: block.id,
        content: JSON.stringify(result)
      });
      actions.push({ type: block.name, input: block.input, result });
    }
  }

  if (toolResults.length > 0 && depth < MAX_TOOL_ROUNDS) {
    const updatedMessages = [
      ...messages,
      { role: 'assistant', content },
      { role: 'user', content: toolResults }
    ];

    const followUpData = await callAnthropic(updatedMessages, onStatusUpdate, voiceMode);
    const followUpResult = await processAIResponse(followUpData, updatedMessages, navigate, onStatusUpdate, voiceMode, depth + 1);

    return {
      text: followUpResult.text,
      actions: [...actions, ...followUpResult.actions],
      messages: followUpResult.messages
    };
  }

  return {
    text: textResponse,
    actions,
    messages: [
      ...messages,
      { role: 'assistant', content: textResponse }
    ]
  };
};

/**
 * Send a message to the AI and get a response.
 * @param {string} userMessage - The user's message
 * @param {Array} conversationHistory - Previous messages
 * @param {Function} navigate - React Router navigate function
 * @param {Function} onStatusUpdate - Callback for status updates
 * @param {boolean} voiceMode - Whether this is a voice command
 * @returns {{ text: string, actions: Array, messages: Array }}
 */
export const sendMessageToAI = async (userMessage, conversationHistory, navigate, onStatusUpdate, voiceMode = false) => {
  const allMessages = [
    ...conversationHistory,
    { role: 'user', content: userMessage }
  ];

  const trimmedMessages = allMessages.length > 20
    ? allMessages.slice(-20)
    : allMessages;

  try {
    const data = await callAnthropic(trimmedMessages, onStatusUpdate, voiceMode);
    return await processAIResponse(data, trimmedMessages, navigate, onStatusUpdate, voiceMode);
  } catch (error) {
    console.error('Dashboard AI Service Error:', error);
    throw error;
  }
};

export default { sendMessageToAI, executeTool };
